<template>
  <div id="app">
    <SiteHeader />
    <div id="main-content">
      <router-view />
    </div>
    <SocialChat v-if="displayCellInfo" icon :attendants="channels">
      <p slot="header">Assistance personnalisée</p>

      <template v-slot:button>
        <img
          src="https://raw.githubusercontent.com/ktquez/vue-social-chat/master/src/icons/whatsapp.svg"
          alt="icon whatsapp"
          aria-hidden="true"
        />
      </template>
      <small slot="footer">
        <h4>Heures d'ouverture</h4>
        <p>Lundi - Vendredi : 8h00 - 12h30 & 13h00 - 16h30</p>
        <p>Samedi & Dimanche: Fermé</p>
      </small>
    </SocialChat>
    <SiteFooter />
  </div>
</template>

<script>
import { SocialChat } from "vue-social-chat";
import { defineAsyncComponent } from "vue";

// ★ Clés du localStorage à ne JAMAIS effacer au chargement de la page :
//   careers_session_<offre> : le candidat n'a pas à ressaisir de code (7 jours)
//   careers_draft_<offre>   : ses réponses en cours
//   Sans cette exception, chaque rechargement redemandait un code OTP
//   (et bloquait le candidat après 3 envois en une heure).
const PRESERVED_PREFIXES = ["careers_"];

/** Vide le localStorage, sauf les clés listées ou commençant par un préfixe protégé. */
function clearLocalStorageExcept(prefixes = PRESERVED_PREFIXES, keys = []) {
  const all = [];
  for (let i = 0; i < localStorage.length; i++) all.push(localStorage.key(i));
  all
    .filter(
      (k) =>
        k !== null &&
        !keys.includes(k) &&
        !prefixes.some((p) => k.startsWith(p))
    )
    .forEach((k) => localStorage.removeItem(k));
}

export default {
  props: {},
  name: "App",
  components: {
    SiteHeader: defineAsyncComponent(() =>
      import("./components/SiteHeader.vue")
    ),
    SiteFooter: defineAsyncComponent(() =>
      import("./components/SiteFooter.vue")
    ),
    SocialChat,
  },
  data() {
    return {
      displayCellInfo: false,
      channels: [
        {
          app: "whatsapp",
          label: "Accueil",
          name: "Standard",
          number: "22952529898",
          avatar: {
            src: require("@/assets/images/woman.webp"),
            alt: "Standard",
          },
        },
        {
          app: "whatsapp",
          label: "Support Commercial",
          name: "Support Commercial",
          number: "22997627157",
          avatar: {
            src: require("@/assets/images/woman.webp"),
            alt: "Support Commercial",
          },
        },
        {
          app: "whatsapp",
          label: "Support Sinistre",
          name: "Support Sinistre",
          number: "22967294366",
          avatar: {
            src: require("@/assets/images/man.webp"),
            alt: "Support Sinistre",
          },
        },
        {
          app: "whatsapp",
          label: "Support Informatique",
          name: "Support Informatique",
          number: "22997298195",
          avatar: {
            src: require("@/assets/images/man.webp"),
            alt: "Support Informatique",
          },
        },
      ],
    };
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      window.onload = () => {
        try {
          // ★ MODIFIÉ : avant, localStorage.clear() effaçait aussi la
          //   candidature en cours (session et brouillon careers_…)
          clearLocalStorageExcept();
          sessionStorage.clear();
        } catch (e) {
          // Stockage indisponible (navigation privée stricte…) : rien à vider
        }

        if ("caches" in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }
      };
    },
    async clearSiteDataUniversal(options = {}) {
      const {
        reload = true,
        preserveKeys = [], // clés à conserver (token, user…)
        preservePrefixes = PRESERVED_PREFIXES, // ★ préfixes à conserver (candidature en cours)
      } = options;

      // console.log("[ClearSiteData] Démarrage nettoyage...");

      /* =========================
       * 1️⃣ CACHE API (Chrome / Edge / Firefox)
       * ========================= */
      if ("caches" in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        } catch (e) {
          console.warn("Cache API non supporté", e);
        }
      }

      /* =========================
       * 2️⃣ localStorage (TOUS, sauf clés et préfixes conservés)
       * ========================= */
      try {
        clearLocalStorageExcept(preservePrefixes, preserveKeys);
      } catch (e) {
        console.warn("localStorage indisponible", e);
      }

      /* =========================
       * 3️⃣ sessionStorage (TOUS)
       * ========================= */
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn("sessionStorage indisponible", e);
      }

      /* =========================
       * 4️⃣ IndexedDB (Firefox / Chrome / Edge / Safari)
       * ========================= */
      try {
        if (window.indexedDB) {
          // Méthode standard (TOUS navigateurs)
          const dbs = await new Promise((resolve) => {
            if (indexedDB.databases) {
              indexedDB
                .databases()
                .then(resolve)
                .catch(() => resolve([]));
            } else {
              resolve([]); // Safari ancien
            }
          });

          if (dbs.length) {
            await Promise.all(
              dbs.map(
                (db) =>
                  new Promise((res) => {
                    const req = indexedDB.deleteDatabase(db.name);
                    req.onsuccess = req.onerror = req.onblocked = () => res();
                  })
              )
            );
          }
        }
      } catch (e) {
        console.warn("IndexedDB non nettoyé", e);
      }

      /* =========================
       * 5️⃣ Service Workers (Chrome / Edge / Firefox)
       * ========================= */
      if ("serviceWorker" in navigator) {
        try {
          const registrations =
            await navigator.serviceWorker.getRegistrations();

          await Promise.all(registrations.map((r) => r.unregister()));
        } catch (e) {
          console.warn("Service Worker non supprimé", e);
        }
      }

      /* =========================
       * 6️⃣ Cookies JS accessibles
       * ========================= */
      try {
        document.cookie.split(";").forEach((cookie) => {
          const name = cookie.split("=")[0].trim();
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
      } catch (e) {
        console.warn("Cookies JS non supprimés", e);
      }

      // console.log("[ClearSiteData] Nettoyage terminé");

      /* =========================
       * 7️⃣ Reload
       * ========================= */
      if (reload) {
        location.href = location.pathname + "?clear=" + Date.now();
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap");

body {
  font-family: "Open Sans", sans-serif;
  color: #333;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

#app {
  text-align: center;
  display: flex;
  flex-direction: column;
  flex: 1;
}

#main-content {
  padding-top: 70px;
}

.vsc-popup-button {
  background-color: #47a8ff !important;
}

.vsc-popup-header {
  background-color: #47a8ff !important;
  text-align: center;
}
</style>
